/**
 * MembersTab - Tab content for managing organization members
 */
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import apiClient from "../../utils/apiClient";
import {
  Users,
  Shield,
  Crown,
  UserMinus,
  UserCog,
  Clock,
  XCircle,
} from "lucide-react";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", member: "Member" };
const ROLE_ICONS = { owner: Crown, admin: Shield, member: Users };

export default function MembersTab({ orgId, members, setMembers, myRole }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const isAdminOrOwner = myRole === "owner" || myRole === "admin";

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await apiClient.post(`/orgs/${orgId}/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      const res = await apiClient.get(`/orgs/${orgId}/members`);
      setMembers(res.data.members);
    } catch (err) {
      // handled by interceptor
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from the organization?`)) return;
    try {
      await apiClient.post(`/orgs/${orgId}/members/${memberId}/remove`);
      toast.success(`Removed ${email}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err) {
      // handled by interceptor
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await apiClient.post(`/orgs/${orgId}/members/${memberId}/role`, {
        role: newRole,
      });
      toast.success("Role updated");
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
    } catch (err) {
      // handled by interceptor
    }
  };

  const accepted = members.filter((m) => m.invite_status === "accepted");
  const pending = members.filter((m) => m.invite_status === "pending");

  return (
    <div className="space-y-8" data-testid="members-tab">
      {isAdminOrOwner && (
        <div className="bg-card border border-border rounded-lg p-5">
          <h3 className="font-medium text-sm mb-3">Invite a team member</h3>
          <form onSubmit={handleInvite} className="flex items-end gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="invite-email" className="text-xs">
                Email address
              </Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div className="w-32 space-y-1.5">
              <Label htmlFor="invite-role" className="text-xs">
                Role
              </Label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full h-10 px-3 bg-card border border-input rounded-md text-sm"
                data-testid="invite-role-select"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <Button
              type="submit"
              disabled={inviting || !inviteEmail}
              data-testid="send-invite-btn"
            >
              {inviting ? "Sending..." : "Send Invite"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            {members.length} / 10 member slots used
          </p>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Active Members ({accepted.length})
        </h3>
        <div className="space-y-2">
          {accepted.map((m) => {
            const Icon = ROLE_ICONS[m.role] || Users;
            return (
              <div
                key={m.id}
                className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3"
                data-testid={`member-row-${m.email}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {m.user_name || m.email}
                    </p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-1 bg-muted rounded-md">
                    {ROLE_LABELS[m.role]}
                  </span>
                  {isAdminOrOwner && m.role !== "owner" && (
                    <div className="flex items-center gap-1">
                      {myRole === "owner" && (
                        <button
                          onClick={() =>
                            handleRoleChange(
                              m.id,
                              m.role === "admin" ? "member" : "admin"
                            )
                          }
                          className="p-1.5 text-muted-foreground hover:text-foreground transition"
                          title={
                            m.role === "admin"
                              ? "Demote to member"
                              : "Promote to admin"
                          }
                          data-testid={`role-toggle-${m.email}`}
                        >
                          <UserCog className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(m.id, m.email)}
                        className="p-1.5 text-muted-foreground hover:text-red-600 transition"
                        title="Remove member"
                        data-testid={`remove-member-${m.email}`}
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Pending Invites ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 opacity-70"
                data-testid={`pending-row-${m.email}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm">{m.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Invited · Expires{" "}
                      {m.invite_expires_at
                        ? new Date(m.invite_expires_at).toLocaleDateString()
                        : "–"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-md">
                    Pending
                  </span>
                  {isAdminOrOwner && (
                    <button
                      onClick={() => handleRemove(m.id, m.email)}
                      className="p-1.5 text-muted-foreground hover:text-red-600 transition"
                      title="Cancel invite"
                      data-testid={`cancel-invite-${m.email}`}
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
